import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
}

export function Toast({ message, type, onClose, duration = 4000 }: ToastProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'from-emerald-500 to-teal-600',
          icon: <CheckCircle className="w-5 h-5" />,
        };
      case 'error':
        return {
          bg: 'from-red-500 to-rose-600',
          icon: <XCircle className="w-5 h-5" />,
        };
      case 'warning':
        return {
          bg: 'from-orange-500 to-amber-600',
          icon: <AlertCircle className="w-5 h-5" />,
        };
      case 'info':
        return {
          bg: 'from-blue-500 to-cyan-600',
          icon: <Info className="w-5 h-5" />,
        };
    }
  };

  const styles = getStyles();

  return (
    <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 animate-slideDown">
      <div className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${styles.bg} shadow-2xl max-w-md`}>
        <div className="relative px-6 py-4 flex items-center gap-4">
          <div className="flex-shrink-0 text-white">
            {styles.icon}
          </div>
          <div className="flex-1 text-white font-medium">
            {message}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Toast Container Component
interface ToastContainerProps {
  children: React.ReactNode;
}

export function ToastContainer({ children }: ToastContainerProps) {
  return <>{children}</>;
}
